import { useState, useEffect } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Calendar, Clock, Search, TrendingUp, ArrowRight, Star, ChevronRight, Folder, Flame, BookOpen, MapPin, UtensilsCrossed, Lightbulb, Plane, Newspaper, Map, Camera, Coffee, Heart, Globe } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { newsApi } from '../../lib/api'
import { formatDate } from '../../lib/utils'

// Helper to render category icon
const iconMap: Record<string, any> = {
    'bookopen': BookOpen, 'book-open': BookOpen,
    'mappin': MapPin, 'map-pin': MapPin,
    'utensilscrossed': UtensilsCrossed, 'utensils-crossed': UtensilsCrossed,
    'lightbulb': Lightbulb, 'plane': Plane, 'newspaper': Newspaper,
    'folder': Folder, 'map': Map, 'camera': Camera, 'coffee': Coffee,
    'star': Star, 'heart': Heart, 'globe': Globe,
}
const renderCatIcon = (iconName: string | undefined, className = 'w-4 h-4') => {
    if (!iconName) return <Folder className={className} />
    const IconComponent = iconMap[iconName.toLowerCase().trim()] || Folder
    return <IconComponent className={className} />
}

interface Article {
    id: number
    slug: string
    title_en: string
    title_vi: string
    excerpt_en: string
    excerpt_vi: string
    category: string
    featured: boolean
    featured_image?: string
    views: number
    created_at: string
}

interface Category {
    id: number
    slug: string
    name_en: string
    name_vi: string
    icon: string
}

export default function NewsPage() {
    useTranslation()
    const { language } = useLanguageStore()
    const { category: urlCategory } = useParams()
    const [searchParams] = useSearchParams()
    const searchFromUrl = searchParams.get('search') || ''

    const [categories, setCategories] = useState<Category[]>([])
    const [articles, setArticles] = useState<Article[]>([])
    const [searchQuery, setSearchQuery] = useState(searchFromUrl)
    const [loading, setLoading] = useState(true)

    // Fetch categories from API
    useEffect(() => {
        fetch('/api/categories')
            .then(r => r.json())
            .then(data => setCategories(data.categories || []))
    }, [])

    // Fetch articles
    useEffect(() => {
        const fetchNews = async () => {
            setLoading(true)
            try {
                const params: any = { status: 'published', limit: 50 }
                if (urlCategory) {
                    params.category = urlCategory
                }
                const response = await newsApi.list(params)
                if (response.data?.success && response.data.data) {
                    setArticles(response.data.data)
                }
            } catch (error) {
                console.error('Failed to fetch news:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchNews()
    }, [urlCategory])

    const getTitle = (article: Article) => language === 'en' ? article.title_en : (article.title_vi || article.title_en)
    const getExcerpt = (article: Article) => language === 'en' ? article.excerpt_en : (article.excerpt_vi || article.excerpt_en)
    const getCatName = (c: Category) => language === 'en' ? c.name_en : c.name_vi

    const currentCategory = categories.find(c => c.slug === urlCategory)

    // Filter by search
    const filteredArticles = articles.filter(a => {
        const title = getTitle(a)
        return title.toLowerCase().includes(searchQuery.toLowerCase())
    })

    const featuredArticle = filteredArticles.find(a => a.featured) || filteredArticles[0]
    const otherArticles = filteredArticles.filter(a => a.id !== featuredArticle?.id)
    const trendingArticles = [...articles].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5)

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-primary-700 to-primary-900 text-white py-12">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl">
                        {/* Breadcrumb */}
                        <div className="flex items-center gap-2 text-primary-200 text-sm mb-4">
                            <Link to="/" className="hover:text-white">Home</Link>
                            <ChevronRight className="w-4 h-4" />
                            <Link to="/blog" className="hover:text-white">Blog</Link>
                            {currentCategory && (
                                <>
                                    <ChevronRight className="w-4 h-4" />
                                    <span className="text-white">{getCatName(currentCategory)}</span>
                                </>
                            )}
                        </div>

                        <h1 className="text-3xl md:text-4xl font-bold mb-4">
                            {currentCategory ? (
                                <span className="flex items-center gap-3">
                                    {renderCatIcon(currentCategory.icon, 'w-8 h-8')}
                                    {getCatName(currentCategory)}
                                </span>
                            ) : (
                                language === 'en' ? 'Travel Blog' : 'Blog Du Lịch'
                            )}
                        </h1>
                        <p className="text-primary-100 mb-6">
                            {currentCategory
                                ? (language === 'en' ? `Browse all articles in ${getCatName(currentCategory)}` : `Xem tất cả bài viết trong ${getCatName(currentCategory)}`)
                                : (language === 'en' ? 'Discover tips, guides and stories from Phú Quốc' : 'Khám phá mẹo, hướng dẫn và câu chuyện từ Phú Quốc')
                            }
                        </p>

                        {/* Search */}
                        <div className="relative max-w-lg">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="search"
                                placeholder={language === 'en' ? 'Search articles...' : 'Tìm bài viết...'}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-900 bg-white shadow-lg focus:ring-4 focus:ring-primary-300"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                {/* Category Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-4 mb-6 -mx-4 px-4">
                    <Link
                        to="/blog"
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium whitespace-nowrap transition ${!urlCategory
                            ? 'bg-primary-600 text-white shadow-lg'
                            : 'bg-white border hover:border-primary-300 hover:text-primary-600'
                            }`}
                    >
                        <Flame className="w-4 h-4" /> {language === 'en' ? 'All Posts' : 'Tất cả'}
                    </Link>
                    {categories.map((cat) => (
                        <Link
                            key={cat.id}
                            to={`/blog/${cat.slug}`}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium whitespace-nowrap transition ${urlCategory === cat.slug
                                ? 'bg-primary-600 text-white shadow-lg'
                                : 'bg-white border hover:border-primary-300 hover:text-primary-600'
                                }`}
                        >
                            {renderCatIcon(cat.icon)} {getCatName(cat)}
                        </Link>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {loading ? (
                            <div className="space-y-4">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                                        <div className="h-48 bg-gray-200 rounded-lg mb-4" />
                                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
                                        <div className="h-4 bg-gray-200 rounded w-1/2" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                {/* Featured Article */}
                                {featuredArticle && (
                                    <Link
                                        to={`/blog/${featuredArticle.category}/${featuredArticle.slug}`}
                                        className="block group bg-white rounded-2xl overflow-hidden border hover:shadow-xl transition"
                                    >
                                        <div className="relative h-64 bg-gradient-to-br from-primary-200 to-primary-400">
                                            {featuredArticle.featured_image && (
                                                <img
                                                    src={featuredArticle.featured_image}
                                                    alt={getTitle(featuredArticle)}
                                                    className="w-full h-full object-cover"
                                                />
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                            <div className="absolute top-4 left-4">
                                                <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-medium flex items-center gap-1">
                                                    <Star className="w-3 h-3" /> {language === 'en' ? 'Featured' : 'Nổi bật'}
                                                </span>
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                                <span className="text-sm opacity-80 flex items-center gap-1">
                                                    {renderCatIcon(categories.find(c => c.slug === featuredArticle.category)?.icon, 'w-3 h-3')}
                                                    {' '}
                                                    {getCatName(categories.find(c => c.slug === featuredArticle.category) || {} as Category)}
                                                </span>
                                                <h2 className="text-2xl font-bold mt-1 group-hover:text-orange-300 transition">
                                                    {getTitle(featuredArticle)}
                                                </h2>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <p className="text-gray-600 line-clamp-2 mb-3">{getExcerpt(featuredArticle)}</p>
                                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    {formatDate(featuredArticle.created_at, language)}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-4 h-4" /> 5 min
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                )}

                                {/* Articles Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    {otherArticles.map((article) => (
                                        <Link
                                            key={article.id}
                                            to={`/blog/${article.category}/${article.slug}`}
                                            className="group bg-white rounded-xl overflow-hidden border hover:shadow-lg transition"
                                        >
                                            <div className="h-40 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                                                {article.featured_image && (
                                                    <img
                                                        src={article.featured_image}
                                                        alt={getTitle(article)}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                                                    />
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <span className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded-full font-medium flex items-center gap-1 w-fit">
                                                    {renderCatIcon(categories.find(c => c.slug === article.category)?.icon, 'w-3 h-3')}
                                                    {' '}
                                                    {getCatName(categories.find(c => c.slug === article.category) || {} as Category)}
                                                </span>
                                                <h3 className="font-bold mt-2 mb-1 group-hover:text-primary-600 transition line-clamp-2">
                                                    {getTitle(article)}
                                                </h3>
                                                <p className="text-gray-600 text-sm line-clamp-2 mb-2">{getExcerpt(article)}</p>
                                                <span className="text-xs text-gray-500">
                                                    {formatDate(article.created_at, language)}
                                                </span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>

                                {filteredArticles.length === 0 && (
                                    <div className="text-center py-12 text-gray-500">
                                        {language === 'en' ? 'No articles found' : 'Không tìm thấy bài viết'}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Sidebar */}
                    <aside className="space-y-6">
                        {/* Categories */}
                        <div className="bg-white rounded-xl border p-5">
                            <h3 className="font-bold mb-4 flex items-center gap-2">
                                <Folder className="w-4 h-4" /> {language === 'en' ? 'Categories' : 'Danh mục'}
                            </h3>
                            <div className="space-y-2">
                                {categories.map((cat) => (
                                    <Link
                                        key={cat.id}
                                        to={`/blog/${cat.slug}`}
                                        className={`flex items-center justify-between p-2 rounded-lg transition ${urlCategory === cat.slug ? 'bg-primary-50 text-primary-600' : 'hover:bg-gray-50'
                                            }`}
                                    >
                                        <span className="flex items-center gap-2">
                                            {renderCatIcon(cat.icon)}
                                            <span className="text-sm">{getCatName(cat)}</span>
                                        </span>
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Trending */}
                        <div className="bg-white rounded-xl border p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <TrendingUp className="w-5 h-5 text-orange-500" />
                                <h3 className="font-bold">{language === 'en' ? 'Trending' : 'Xu hướng'}</h3>
                            </div>
                            <div className="space-y-3">
                                {trendingArticles.map((article, idx) => (
                                    <Link
                                        key={article.id}
                                        to={`/blog/${article.category}/${article.slug}`}
                                        className="flex gap-3 group"
                                    >
                                        <span className="text-xl font-bold text-gray-200 group-hover:text-primary-300 transition w-6">
                                            {idx + 1}
                                        </span>
                                        <div>
                                            <h4 className="text-sm font-medium group-hover:text-primary-600 transition line-clamp-2">
                                                {getTitle(article)}
                                            </h4>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Newsletter */}
                        <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl p-5 text-white">
                            <Star className="w-8 h-8 mb-3 text-yellow-300" />
                            <h3 className="font-bold mb-2">
                                {language === 'en' ? 'Stay Updated' : 'Cập nhật tin mới'}
                            </h3>
                            <p className="text-primary-100 text-sm mb-4">
                                {language === 'en' ? 'Get travel tips in your inbox' : 'Nhận mẹo du lịch qua email'}
                            </p>
                            <input type="email" placeholder="Email" className="w-full px-4 py-2 rounded-lg mb-3 text-gray-900" />
                            <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white text-primary-600 rounded-lg font-medium hover:bg-gray-100 transition">
                                {language === 'en' ? 'Subscribe' : 'Đăng ký'}
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    )
}
